import {z, ZodTypeAny} from 'zod';
import {
  BatchRenameWidgetContentPart,
  CopilotContext,
  Tool,
  UserConfirmationWidgetContent,
} from '../copilot.types';
import {CopilotWidgetType} from '../copilot.enums';
import {RenameWidgetContent} from '../examples/blockingWidgetExample/tools/renameTool/renameTool.types';
import {ShowBooksWidgetContent} from '../examples/nonBlockingWidgetExample/tools/showBooksTool/showBooksTool.types';
import {inlineWidgetSchema} from 'common/src/modules/analytics/copilot/copilotAnalytics.schemas';
import {excelToCampaignSummaryContentPartSchema} from 'common/src/modules/campaigns/copilot/agents/excelToCampaignAgent/excelToCampaignAgent.schemas';
import {AssetDisplayAndSelectWidgetContent} from 'common/src/modules/creativeTemplates/copilot/widgets/AssetDisplayAndSelectWidget.types';
import {
  CampaignInsightsCopilotContextData,
  CampaignLogicCopilotContextData,
  ConceptPlanCopilotContextData,
} from 'common/src/modules/campaigns/copilot/copilotCampaigns.types';

type CLCopilotContextEntry<T> = {
  data: T;
  tools: Tool<ZodTypeAny>[];
};

enum CopilotContextKey {
  CampaignWorkflow = 'campaign',
  ConceptPlan = 'concept-plan',
  CampaignLogic = 'campaign-logic',
  CampaignInsights = 'campaign-insights',
  MovieComposition = 'movie-composition',
}

interface CLCopilotContext extends Omit<CopilotContext, 'entries'> {
  entries: {
    [CopilotContextKey.CampaignWorkflow]?: CLCopilotContextEntry<null>;
    [CopilotContextKey.ConceptPlan]?: CLCopilotContextEntry<ConceptPlanCopilotContextData>;
    [CopilotContextKey.CampaignLogic]?: CLCopilotContextEntry<CampaignLogicCopilotContextData>;
    [CopilotContextKey.CampaignInsights]?: CLCopilotContextEntry<CampaignInsightsCopilotContextData>;
  };
}

type CLCopilotContextData =
  | ConceptPlanCopilotContextData
  | CampaignLogicCopilotContextData
  | CampaignInsightsCopilotContextData;

interface CopilotWidgetComponent<T = unknown> {
  widgetContent: T;
  isDisabled?: boolean;
  resolve?: (message: string) => void;
}

export type CopilotWidget =
  // example widgets
  | {type: CopilotWidgetType.RenameWidget; content: RenameWidgetContent}
  | {type: CopilotWidgetType.ShowBooksWidget; content: ShowBooksWidgetContent}

  // real widgets
  | z.infer<typeof inlineWidgetSchema>
  | z.infer<typeof excelToCampaignSummaryContentPartSchema>
  | BatchRenameWidgetContentPart
  | {
      type: CopilotWidgetType.AssetDisplayAndSelectWidget;
      content: AssetDisplayAndSelectWidgetContent;
    }
  | {
      type: CopilotWidgetType.UserConfirmationWidget;
      content: UserConfirmationWidgetContent;
    };

export {CLCopilotContext, CLCopilotContextData, CopilotContextKey, CopilotWidgetComponent};
